import React from 'react';
import { Platform, View, StyleSheet, useWindowDimensions } from 'react-native';

import { useResponsive } from '../hooks/useResponsive';
import { spacing } from '../theme';

/**
 * Below this width (native only), UOM and actions stack vertically with left alignment.
 */
export const PERSISTED_STEP_UOM_ACTION_COMPACT_MAX_WIDTH = 640;

export function useIsCompactUomActionBar(
  maxWidth: number = PERSISTED_STEP_UOM_ACTION_COMPACT_MAX_WIDTH,
): boolean {
  const { width } = useWindowDimensions();
  const { isSmallScreen } = useResponsive();
  // Native apps are mobile-first — always stack step title above UOM/actions (matches Opportunity).
  if (Platform.OS !== 'web') {
    return true;
  }
  // Web: window width is the monitor, not the step column — use tablet breakpoint.
  return isSmallScreen || width <= maxWidth;
}

export type PersistedStepUomActionRowProps = {
  uomSlot: React.ReactNode;
  actionsSlot: React.ReactNode;
  /** Viewport width at or below which UOM and actions stack (default 640). */
  compactMaxWidth?: number;
  testID?: string;
};

/**
 * Shared layout for Request Detail step chrome: UOM selector + Advanced/PDF (and similar).
 * Wide: one row — UOM left, actions trailing right. Compact: stacked, left-aligned.
 */
export const PersistedStepUomActionRow: React.FC<
  PersistedStepUomActionRowProps
> = ({
  uomSlot,
  actionsSlot,
  compactMaxWidth = PERSISTED_STEP_UOM_ACTION_COMPACT_MAX_WIDTH,
  testID,
}) => {
  const isCompact = useIsCompactUomActionBar(compactMaxWidth);
  if (uomSlot == null && actionsSlot == null) return null;

  return (
    <View
      style={[styles.shell, isCompact && styles.shellCompact]}
      testID={testID}
    >
      <View style={[styles.uomSlot, isCompact && styles.uomSlotCompact]}>
        {uomSlot}
      </View>
      {actionsSlot != null ? (
        <View
          style={[styles.actionsSlot, isCompact && styles.actionsSlotCompact]}
        >
          {actionsSlot}
        </View>
      ) : null}
    </View>
  );
};

PersistedStepUomActionRow.displayName = 'PersistedStepUomActionRow';

const styles = StyleSheet.create({
  shell: {
    width: '100%',
    marginBottom: spacing.belowUomRow,
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.fieldColumnGapWeb,
    columnGap: spacing.fieldColumnGapWeb,
  },
  uomSlot: {
    maxWidth: 240,
    flexShrink: 0,
    minWidth: 0,
  },
  actionsSlot: {
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: spacing.md,
    columnGap: spacing.md,
    flexShrink: 0,
    flexGrow: 0,
    minWidth: 0,
  },
  shellCompact: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    flexWrap: 'nowrap',
    gap: spacing.belowUomRow,
    rowGap: spacing.belowUomRow,
  },
  uomSlotCompact: {
    maxWidth: '100%',
  },
  actionsSlotCompact: {
    marginLeft: 0,
    alignSelf: 'stretch',
    justifyContent: 'flex-start',
  },
});
